import { defineStore } from 'pinia';
import { ref } from 'vue';
import type {
  IOnboardingImage,
  IFeaturedImagesResponse,
  IOnboardingImagesSearchQuery,
  IUpdateOnboardingImage,
  IUpdateOnboardingOrderBody,
  IOnboardingImageResponse,
  IOnsearchImagesResponse,
} from '@/types/onboarding.types';
import { onboardingCommerceImage } from '@/services/onboarding.services';

/**
 * Store Pinia pour gérer les images d'onboarding des commerces
 */
export const useOnboardingStore = defineStore('onboarding', () => {
  /**
   * Liste des images featured / onboarding
   */
  const featuredImages = ref<IFeaturedImagesResponse | null>(null);

  /**
   * Résultat de recherche des images onboarding
   */
  const searchResults = ref<IFeaturedImagesResponse | null>(null);

  /**
   * État de chargement global
   */
  const loading = ref(false);

  /**
   * Erreur globale
   */
  const error = ref<any>(null);

  /**
   * Créer une image d'onboarding
   */
  const createOnboardingImage = async (data: IOnboardingImage) => {
    try {
      loading.value = true;
      const response =
        await onboardingCommerceImage.createOnboardingCommerceImage(data);
      return response;
    } catch (err) {
      error.value = err;
      return Promise.reject(err);
    } finally {
      loading.value = false;
    }
  };

  /**
   * Récupérer les images featured d’un commerce
   */
  const getFeaturedImages = async (): Promise<IFeaturedImagesResponse> => {
    try {
      loading.value = true;
      const response =
        await onboardingCommerceImage.getCommercesFeaturedImages();
      featuredImages.value = response;
      return response;
    } catch (err) {
      error.value = err;
      return Promise.reject(err);
    } finally {
      loading.value = false;
    }
  };

  /**
   * Recherche des images onboarding avec filtres
   */
  const searchOnboardingImages = async (): Promise<IOnsearchImagesResponse> => {
    try {
      loading.value = true;
      const response =
        await onboardingCommerceImage.getSearchCommercesOnboardingImages();

      return response;
    } catch (err) {
      error.value = err;
      return Promise.reject(err);
    } finally {
      loading.value = false;
    }
  };

  /**
   * Mettre à jour une image d'onboarding
   */
  const updateOnboardingImage = async (data: any): Promise<any> => {
    try {
      loading.value = true;
      return await onboardingCommerceImage.updateOnboardingImage(data);
    } catch (err) {
      error.value = err;
      return Promise.reject(err);
    } finally {
      loading.value = false;
    }
  };

  /**
   * Mettre à jour l'ordre des images d'onboarding
   */
  const updateOnboardingImagesOrder = async (
    body: IUpdateOnboardingOrderBody,
  ): Promise<any> => {
    try {
      loading.value = true;
      return await onboardingCommerceImage.updateOnboardingImagesOrder(body);
    } catch (err) {
      error.value = err;
      return Promise.reject(err);
    } finally {
      loading.value = false;
    }
  };

  /**
   * Supprimer toutes les images d'onboarding d’un commerce
   */
  const deleteAllOnboardingImages = async (): Promise<any> => {
    try {
      loading.value = true;
      return await onboardingCommerceImage.deleteAllOnboardingImages();
    } catch (err) {
      error.value = err;
      return Promise.reject(err);
    } finally {
      loading.value = false;
    }
  };

  /**
   * Supprimer une image featured par ID
   */
  const deleteFeaturedImageById = async (): Promise<any> => {
    try {
      return await onboardingCommerceImage.deleteFeaturedImageById();
    } catch (err) {
      error.value = err;
      return Promise.reject(err);
    } finally {
      loading.value = false;
    }
  };

  return {
    featuredImages,
    searchResults,
    loading,
    error,
    createOnboardingImage,
    getFeaturedImages,
    searchOnboardingImages,
    updateOnboardingImage,
    updateOnboardingImagesOrder,
    deleteAllOnboardingImages,
    deleteFeaturedImageById,
  };
});
